import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Button, Heading, PageShell, Text } from "@/components/ui";
import { apiErrorText } from "@/lib/api-error";
import { legalPath } from "@/lib/legal";
import { logoutSession } from "@/services/api/auth";
import { acceptTerms, fetchTermsStatus, type TermsStatus } from "@/services/api/terms";
import { TERMS_REQUIRED_EVENT } from "@/services/http/terms-required";
import { useAuthStore } from "@/stores/authStore";

/**
 * Pages a member who has not accepted yet may still open: the two texts, the sign-in pages and
 * (review F2) Settings → Privacy, where someone who does not agree exports or deletes their data,
 * and the page that cancels a deletion.
 */
const OPEN_PAGES = [
  "terms",
  "privacy",
  "login",
  "register",
  "verify-email",
  "reset-password",
  "settings/privacy",
  "account/restore",
];

/**
 * H4 (decisions.md §34): a signed-in member of this school who has not accepted the current
 * Terms of use + Privacy notice sees only the accept screen (the API refuses everything else
 * with 403 TERMS_NOT_ACCEPTED anyway). Shown right after sign-in (the session says
 * `termsRequired`) and on the next app load of someone already signed in (asked from the server,
 * so a stored session from before H4 is checked too). Every school user is asked (MT-1 S16).
 */
export function TermsGate({
  tenantSlug,
  schoolName,
  children,
}: {
  tenantSlug: string;
  schoolName: string;
  children: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const pathname = useLocation().pathname;
  // MT-1 AUTH-009 S16: every school user accepts the school's terms (no platform exemption).
  const applies = isAuthenticated && !!user && user.tenantSlug === tenantSlug;

  const statusQuery = useQuery({
    queryKey: ["terms-status", user?.id, user?.tenantId],
    queryFn: fetchTermsStatus,
    enabled: applies,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const queryClient = useQueryClient();
  const userId = user?.id;
  const tenantId = user?.tenantId;
  // Review F11: an API call refused with 403 TERMS_NOT_ACCEPTED (new version mid-session) shows
  // the accept screen at once; the server is asked again to confirm.
  useEffect(() => {
    if (!applies) return;
    const onRequired = () => {
      queryClient.setQueryData<TermsStatus>(["terms-status", userId, tenantId], (old) => ({
        version: old?.version ?? "",
        termsRequired: true,
      }));
      void queryClient.invalidateQueries({ queryKey: ["terms-status", userId, tenantId] });
    };
    window.addEventListener(TERMS_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(TERMS_REQUIRED_EVENT, onRequired);
  }, [applies, queryClient, userId, tenantId]);

  const segments = pathname.split("/");
  const page = segments[3] ?? "";
  const subPage = `${page}/${segments[4] ?? ""}`;
  if (!applies || OPEN_PAGES.includes(page) || OPEN_PAGES.includes(subPage)) {
    return <>{children}</>;
  }

  // The server's answer wins; until it arrives, what the sign-in said.
  const required = statusQuery.data?.termsRequired ?? user.termsRequired;
  if (required === true) {
    return <TermsAcceptScreen tenantSlug={tenantSlug} schoolName={schoolName} />;
  }
  if (required === undefined && statusQuery.isPending) {
    return null;
  }
  return <>{children}</>;
}

function TermsAcceptScreen({ tenantSlug, schoolName }: { tenantSlug: string; schoolName: string }) {
  const t = useTranslations("legal.accept");
  const tRoot = useTranslations();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const clearSession = useAuthStore((s) => s.clearSession);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await acceptTerms();
      const current = useAuthStore.getState().user;
      if (current) useAuthStore.setState({ user: { ...current, termsRequired: false } });
      queryClient.setQueryData<TermsStatus>(
        ["terms-status", current?.id, current?.tenantId],
        (old) => ({ version: old?.version ?? "", termsRequired: false }),
      );
      // Pages that asked while the screen was up got 403: ask again.
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] !== "terms-status",
      });
    } catch (err) {
      setError(apiErrorText(err, tRoot, "legal.accept.failed", { namespace: "auth.errors" }));
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    try {
      await logoutSession();
    } finally {
      clearSession();
      navigate(`/t/${tenantSlug}/login`);
    }
  }

  return (
    <PageShell width="form" data-testid="terms-accept-screen">
      <Heading as="h1" level="page">
        {t("title")}
      </Heading>
      <Text className="mt-4" size="base">
        {t("intro", { school: schoolName })}
      </Text>
      <ul className="mt-4 list-disc space-y-1 pl-5 text-sm">
        <li>
          <Link
            to={legalPath(tenantSlug, "terms")}
            target="_blank"
            className="underline"
            data-testid="terms-accept-terms-link"
          >
            {t("readTerms")}
          </Link>
        </li>
        <li>
          <Link
            to={legalPath(tenantSlug, "privacy")}
            target="_blank"
            className="underline"
            data-testid="terms-accept-privacy-link"
          >
            {t("readPrivacy")}
          </Link>
        </li>
      </ul>
      <Text className="mt-4" tone="secondary">
        {t("summary")}
      </Text>
      <Text className="mt-2" tone="secondary">
        {t("disagree")}{" "}
        <Link
          to={`/t/${tenantSlug}/settings/privacy`}
          className="underline"
          data-testid="terms-accept-privacy-settings-link"
        >
          {t("exportOrDelete")}
        </Link>
      </Text>
      <label className="mt-6 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          data-testid="terms-accept-checkbox"
        />
        <span>{t("checkbox")}</span>
      </label>
      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      ) : null}
      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          type="button"
          disabled={!agreed || busy}
          onClick={() => void accept()}
          data-testid="terms-accept-submit"
        >
          {busy ? t("working") : t("submit")}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void signOut()}
          data-testid="terms-accept-sign-out"
        >
          {t("signOut")}
        </Button>
      </div>
    </PageShell>
  );
}
